import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  GET_VESSEL_NOMINATION,
  LIST_VESSEL_NOMINATIONS,
  SAVE_VESSEL_NOMINATION,
  SUBMIT_VESSEL_NOMINATION,
  UPDATE_VESSEL_NOMINATION,
} from '../graphql/vessel-nomination.graphql';
import {
  GetVesselNominationActions,
  ListVesselNominationsActions,
  SaveVesselNominationActions,
  SubmitVesselNominationActions,
  UpdateVesselNominationActions,
} from './vessel-nomination.actions';
import {
  VesselNominationGqlResponse,
  VesselNominationPageResponse,
} from '../types/vessel-nomination.interface';

@Injectable()
export class VesselNominationEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);
  private readonly swalService = inject(SwalService);

  listNominations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListVesselNominationsActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .query<{ listVesselNominations: VesselNominationPageResponse }>({
            query: LIST_VESSEL_NOMINATIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request.pageableParam ?? null,
              statuses: request.statuses ?? null,
              supplierCompanyUuid: request.supplierCompanyUuid ?? null,
            },
          })
          .pipe(
            map((response) =>
              ListVesselNominationsActions.success({
                response: response.data?.listVesselNominations ?? {
                  first: true,
                  hasContent: false,
                  hasNext: false,
                  hasPrevious: false,
                  last: true,
                  number: 0,
                  numberOfElements: 0,
                  size: 10,
                  totalElements: 0,
                  totalPages: 0,
                  content: [],
                },
              }),
            ),
            catchError((error) =>
              of(ListVesselNominationsActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  getNomination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetVesselNominationActions.request),
      switchMap(({ request }) =>
        this.graphqlService
          .query<{ getVesselNomination: VesselNominationGqlResponse }>({
            query: GET_VESSEL_NOMINATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { nominationUuid: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.getVesselNomination;
              if (res?.status) {
                return GetVesselNominationActions.success({ response: res });
              }
              return GetVesselNominationActions.failure({
                error: res?.message ?? 'Failed to retrieve vessel nomination',
              });
            }),
            catchError((error) =>
              of(GetVesselNominationActions.failure({ error })),
            ),
          ),
      ),
    ),
  );

  saveDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaveVesselNominationActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ saveVesselNomination: VesselNominationGqlResponse }>({
            mutation: SAVE_VESSEL_NOMINATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.saveVesselNomination;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Vessel nomination draft saved successfully',
                );
                return SaveVesselNominationActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to save vessel nomination draft',
              );
              return SaveVesselNominationActions.failure({
                error: res?.message ?? 'Failed to save draft',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to save vessel nomination draft',
              );
              return of(SaveVesselNominationActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  updateDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateVesselNominationActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ updateVesselNomination: VesselNominationGqlResponse }>({
            mutation: UPDATE_VESSEL_NOMINATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.updateVesselNomination;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Vessel nomination updated successfully',
                );
                return UpdateVesselNominationActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to update vessel nomination',
              );
              return UpdateVesselNominationActions.failure({
                error: res?.message ?? 'Failed to update draft',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to update vessel nomination',
              );
              return of(UpdateVesselNominationActions.failure({ error }));
            }),
          ),
      ),
    ),
  );

  submitNomination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmitVesselNominationActions.request),
      mergeMap(({ request }) =>
        this.graphqlService
          .mutate<{ submitVesselNomination: VesselNominationGqlResponse }>({
            mutation: SUBMIT_VESSEL_NOMINATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const res = response.data?.submitVesselNomination;
              if (res?.status) {
                this.swalService.success(
                  res.message ?? 'Vessel nomination submitted successfully',
                );
                return SubmitVesselNominationActions.success({ response: res });
              }
              this.swalService.error(
                res?.message ?? 'Failed to submit vessel nomination',
              );
              return SubmitVesselNominationActions.failure({
                error: res?.message ?? 'Failed to submit nomination',
              });
            }),
            catchError((error) => {
              this.swalService.error(
                error?.message ?? 'Failed to submit vessel nomination',
              );
              return of(SubmitVesselNominationActions.failure({ error }));
            }),
          ),
      ),
    ),
  );
}
